import { useContext, useMemo, type ReactNode } from "react";
import { DndContext } from "@dnd-kit/core";
import type { Announcer } from "@design-system/LiveRegion/LiveRegion";
import type {
    BoardCardContext,
    BoardCardShortcut,
    BoardColumnDef,
    BoardLaneDef,
    BoardLock,
    BoardMove,
    BoardMoveBatch,
    BoardMoveCheck,
    BoardMovedItemGetter,
    BoardMoveVerdict,
    BoardPlaceRule,
    BoardSortHint,
} from "./Board.types";
import type { BoardColumnAddingResolver } from "./boardAdd";
import type { BoardKeyboardCopy } from "./boardAnnouncements";
import {
    getCellEntries,
    getCellKey,
    type BoardCellEntry,
    type BoardCells,
} from "./boardCells";
import type { BoardCollapse } from "./boardCollapse";
import { BoardDropCell, BoardDropStrip } from "./BoardDropZones";
import { BoardGroupOverlay } from "./BoardGroupOverlay";
import { BoardLayout } from "./BoardLayout";
import { locateBoardEntry } from "./boardMove";
import { createBoardDropAnimation, getSortableTransition } from "./boardMotion";
import { BoardOrphanedDragGuard } from "./BoardOrphanedDragGuard";
import { BoardSortableCard, type BoardSlotLook } from "./BoardSortableCard";
import { getSortHintCopy } from "./boardSortHintCopy";
import { useBoardDndConfig } from "./useBoardDndConfig";
import { useBoardDrag } from "./useBoardDrag";
import { useBoardHoverExpand } from "./useBoardHoverExpand";
import { useBoardLandingScroll } from "./useBoardLandingScroll";
import { BoardSelectionContext, NO_ITEM_IDS } from "./useBoardSelection";
import dragStyles from "./BoardDrag.module.scss";

interface DraggableBoardProps<TItem> {
    "aria-label": string;
    cells: BoardCells<TItem>;
    columns: readonly BoardColumnDef[];
    lanes?: readonly BoardLaneDef[];
    hasCommandIsland: boolean;
    collapse: BoardCollapse;
    hasGapAboveFirst: boolean;
    renderCard: (item: TItem, context: BoardCardContext) => ReactNode;
    getItemLabel: (item: TItem) => string;
    compareItems?: (first: TItem, second: TItem) => number;
    sortHint?: BoardSortHint;
    getMovedItem?: BoardMovedItemGetter<TItem>;
    canPlaceBetween: boolean;
    canPlaceAt?: BoardPlaceRule<TItem>;
    emptyColumnLabel: string;
    onMove: (move: BoardMove<TItem>) => void;
    onMoveMany?: (batch: BoardMoveBatch<TItem>) => void;
    hasMultiValueLanes: boolean;
    getLock?: (item: TItem) => BoardLock | null;
    canMove?: (check: BoardMoveCheck<TItem>) => BoardMoveVerdict;
    getColumnAdding: BoardColumnAddingResolver<TItem>;
    announcer: Announcer;
    keyboardCopy: BoardKeyboardCopy;
    cardShortcuts: readonly BoardCardShortcut[];
    initialFocusedItemId?: string;
}

const getPinnedKeys = (...keys: Array<string | null>) =>
    keys.flatMap((key) => (key === null ? [] : [key]));

/** `Board` with drag and drop: same layout, plus drag context and overlay. */
export const DraggableBoard = <TItem,>({
    "aria-label": ariaLabel,
    cells,
    columns,
    lanes,
    hasCommandIsland,
    collapse,
    hasGapAboveFirst,
    renderCard,
    getItemLabel,
    compareItems,
    sortHint,
    getMovedItem,
    canPlaceBetween,
    canPlaceAt,
    emptyColumnLabel,
    onMove,
    onMoveMany,
    hasMultiValueLanes,
    getLock,
    canMove,
    getColumnAdding,
    announcer,
    keyboardCopy,
    cardShortcuts,
    initialFocusedItemId,
}: Readonly<DraggableBoardProps<TItem>>) => {
    const selection = useContext(BoardSelectionContext);
    const drag = useBoardDrag({
        cells,
        columns,
        lanes,
        collapse,
        getItemLabel,
        compareItems,
        getMovedItem,
        canPlaceBetween,
        canPlaceAt,
        onMove,
        onMoveMany,
        selectedItemIds: selection?.selectedItemIds ?? NO_ITEM_IDS,
        hasMultiValueLanes,
        getLock,
        canMove,
        announceMessage: announcer.announce,
    });
    const dndConfig = useBoardDndConfig({
        renderedCells: drag.renderedCells,
        stepKeyboard: drag.stepKeyboard,
        isSettling: drag.isSettling,
    });
    const layoutCollapse = useBoardHoverExpand({
        collapse,
        hoveredColumnId: drag.isPointerDrag
            ? (drag.targetPlacement?.columnId ?? null)
            : null,
    });
    const land = useBoardLandingScroll(drag);
    const dropAnimation = useMemo(
        () =>
            createBoardDropAnimation({
                motion: drag.motion,
                settlingClassName: dragStyles.settling,
                land,
                onSettled: drag.settle,
            }),
        [drag.motion, drag.settle, land],
    );
    const transition =
        drag.isKeyboardDrag || drag.isSlotInstant
            ? null
            : getSortableTransition(drag.motion);
    const sortHintCopy = getSortHintCopy(sortHint, {
        canPlaceBetween,
        placeRefusal: drag.placeRefusal,
    });
    const cellSortHint =
        sortHintCopy === undefined || drag.activeKey === null
            ? undefined
            : {
                  copy: sortHintCopy,
                  isPlacing: drag.isPlacingInSort,
                  slotKey: drag.activeKey,
              };
    const activeEntry =
        drag.activeKey === null
            ? undefined
            : locateBoardEntry(drag.renderedCells, drag.activeKey)?.entry;
    const activeItemId = activeEntry?.itemId;
    const overlayEntry = drag.isPointerDrag ? activeEntry : undefined;
    const getLook = ({
        placementKey,
        itemId,
    }: BoardCellEntry<TItem>): BoardSlotLook => {
        if (placementKey === drag.activeKey) {
            return drag.isKeyboardDrag ? "lifted" : "slot";
        }
        return itemId !== activeItemId && drag.group?.itemIds.has(itemId)
            ? "carried"
            : "card";
    };
    const { landingKeys } = drag;
    const landingIndexes = useMemo(
        () => new Map(landingKeys.map((key, index) => [key, index])),
        [landingKeys],
    );

    const { targetPlacement, focusKey } = drag;
    const windowing = useMemo(
        () => ({ pinnedKeys: getPinnedKeys(drag.activeKey, focusKey) }),
        [drag.activeKey, focusKey],
    );
    const targetLaneId = targetPlacement?.laneId ?? null;
    const pinnedLaneIds = useMemo(
        () => getPinnedKeys(targetLaneId),
        [targetLaneId],
    );
    const getRefusalInLane = (columnId: string) =>
        targetPlacement === null
            ? undefined
            : drag.refusals.get(
                  getCellKey({ columnId, laneId: targetPlacement.laneId }),
              );
    // Strip drops land in the card's origin lane, so that's the cell to check.
    const getStripRefusal = (columnId: string) =>
        drag.refusals.get(getCellKey({ columnId, laneId: drag.originLaneId }));
    const getColumnRefusal = (columnId: string) => {
        const reason = getRefusalInLane(columnId);
        return reason === undefined
            ? undefined
            : { reason, isShown: targetPlacement?.columnId === columnId };
    };

    return (
        <DndContext {...dndConfig} {...drag.handlers}>
            <BoardOrphanedDragGuard
                isPointerDrag={drag.isPointerDrag}
                onOrphaned={drag.releaseOrphanedDrag}
            />
            <BoardLayout
                aria-label={ariaLabel}
                columns={columns}
                lanes={lanes}
                hasCommandIsland={hasCommandIsland}
                cells={cells}
                collapse={layoutCollapse}
                hasGapAboveFirst={hasGapAboveFirst}
                getColumnRefusal={getColumnRefusal}
                getColumnAdding={getColumnAdding}
                pinnedLaneIds={pinnedLaneIds}
                isDragActive={drag.activeKey !== null}
                announcer={announcer}
                keyboardCopy={keyboardCopy}
                cardShortcuts={cardShortcuts}
                initialFocusedItemId={initialFocusedItemId}
                renderCell={({
                    column,
                    lane,
                    placement,
                    labelledBy,
                    isWindowed,
                }) => (
                    <BoardDropCell
                        placement={placement}
                        labelledBy={labelledBy}
                        entries={getCellEntries(drag.renderedCells, placement)}
                        windowing={isWindowed ? windowing : undefined}
                        emptyLabel={
                            lane === null || drag.activeKey !== null
                                ? emptyColumnLabel
                                : null
                        }
                        adding={getColumnAdding(column, lane)}
                        isDragActive={drag.activeKey !== null}
                        refusalReason={drag.refusals.get(getCellKey(placement))}
                        sortHint={
                            drag.sortHintCellKey === getCellKey(placement)
                                ? cellSortHint
                                : undefined
                        }
                        renderEntry={(entry, slot) => (
                            <BoardSortableCard
                                key={entry.placementKey}
                                entry={entry}
                                slot={slot}
                                renderCard={renderCard}
                                look={getLook(entry)}
                                isPointerDrag={drag.isPointerDrag}
                                landingIndex={landingIndexes.get(
                                    entry.placementKey,
                                )}
                                carriedCount={
                                    drag.isKeyboardDrag &&
                                    entry.placementKey === drag.activeKey
                                        ? drag.group?.itemIds.size
                                        : undefined
                                }
                                isSiblingOfDragged={
                                    entry.itemId === activeItemId &&
                                    entry.placementKey !== drag.activeKey
                                }
                                transition={transition}
                                registerActivator={drag.registerActivator}
                                lock={getLock?.(entry.item) ?? null}
                                onLockedPickUp={drag.announceLockedCard}
                            />
                        )}
                    />
                )}
                renderCollapsedColumn={({ column, ...stripProps }) => (
                    <BoardDropStrip
                        columnId={column.id}
                        icon={column.icon}
                        isDropTarget={targetPlacement?.columnId === column.id}
                        refusalReason={getStripRefusal(column.id)}
                        {...stripProps}
                    />
                )}
            />
            <BoardGroupOverlay
                entry={overlayEntry}
                count={drag.group?.itemIds.size ?? 1}
                isSelected={
                    overlayEntry !== undefined &&
                    (selection?.isItemSelected(overlayEntry.itemId) ?? false)
                }
                isRefused={
                    targetPlacement !== null &&
                    drag.refusals.has(getCellKey(targetPlacement))
                }
                dropAnimation={dropAnimation}
                renderCard={renderCard}
            />
        </DndContext>
    );
};
